
import Vue from "vue";
import { mapGetters } from "vuex";
import ChatMessage from "@/components/molecules/ChatMessage.vue";
import ActionButtonGroup, { ActionButtonData } from "@/components/molecules/ActionButtonGroup.vue";
import { logWarn, logError } from "@/lib/utils/logger";
import { ASSIST_MESSAGES } from "@/constants/assistMessages";

// 🆕 より具体的で型安全なインターフェース
interface RequestSuggestion {
  id: "feedback" | "matching" | "catchcopy";
  text: string;
  action: "request-feedback" | "request-matching" | "request-catchcopy";
}

interface AssistRequestContentData {
  feedbackSuggestion: RequestSuggestion;
  matchingSuggestion: RequestSuggestion;
  catchcopySuggestion: RequestSuggestion;
}

interface AssistRequestContentMethods {
  handleRequestClick(buttonText: string): void;
  executeFeedbackAction(): void;
  executeMatchingAction(): void;
  executeCatchcopyAction(): void;
  handleInfoBannerClose(): void;
}

interface AssistRequestContentComputed {
  requestButtons: ActionButtonData[];
  shouldShowInfoBanner: boolean;
}

export default Vue.extend<AssistRequestContentData, AssistRequestContentMethods, AssistRequestContentComputed>({
  name: "AssistRequestContent",
  components: {
    ChatMessage,
    ActionButtonGroup,
  },
  data(): AssistRequestContentData {
    return {
      // 感想依頼
      feedbackSuggestion: {
        id: "feedback",
        text: ASSIST_MESSAGES.FEEDBACK_MANUSCRIPT.requestTitle,
        action: "request-feedback",
      },
      // 応募先検索依頼
      matchingSuggestion: {
        id: "matching",
        text: ASSIST_MESSAGES.MATCHING_COMPANY.requestTitle,
        action: "request-matching",
      },
      // キャッチコピー生成依頼
      catchcopySuggestion: {
        id: "catchcopy",
        text: ASSIST_MESSAGES.GENERATE_CATCHCOPY.requestTitle,
        action: "request-catchcopy",
      },
    };
  },
  computed: {
    // 依頼ボタン群の生成
    requestButtons(): ActionButtonData[] {
      return [
        {
          text: this.feedbackSuggestion.text,
        },
        {
          text: this.matchingSuggestion.text,
        },
        {
          text: this.catchcopySuggestion.text,
        },
      ];
    },

    // Vuex gettersをmapしてバナー表示状態を取得
    ...mapGetters("assistNavigationModule", {
      shouldShowInfoBanner: "shouldShowInfoBanner",
    }),
  },
  methods: {
    // 依頼ボタン押下処理
    handleRequestClick(buttonText: string): void {
      // 押下されたボタンに応じて親コンポーネントにイベントを送信
      if (buttonText === this.feedbackSuggestion.text) {
        this.executeFeedbackAction();
        this.$emit("suggestion-click", this.feedbackSuggestion);
      } else if (buttonText === this.matchingSuggestion.text) {
        this.executeMatchingAction();
        this.$emit("suggestion-click", this.matchingSuggestion);
      } else if (buttonText === this.catchcopySuggestion.text) {
        this.executeCatchcopyAction();
        this.$emit("suggestion-click", this.catchcopySuggestion);
      } else {
        logWarn({ module: "AssistRequestContent", action: "handleRequestClick" }, "予期しないボタンテキスト", {
          buttonText,
          expected: [this.feedbackSuggestion.text, this.matchingSuggestion.text, this.catchcopySuggestion.text],
        });
      }
    },

    // 感想依頼処理
    executeFeedbackAction(): void {
      try {
        // 感想依頼 → 原稿選択画面に遷移
        this.$store.dispatch("assistNavigationModule/showManuscriptSelect");
      } catch (error) {
        logError(
          { module: "AssistRequestContent", action: "executeFeedbackAction" },
          "感想依頼アクション実行エラー",
          error
        );
      }
    },

    // 応募先検索処理
    executeMatchingAction(): void {
      try {
        // 応募先検索 → 原稿選択画面に遷移
        this.$store.dispatch("assistNavigationModule/showManuscriptSelect");
      } catch (error) {
        logError(
          { module: "AssistRequestContent", action: "executeMatchingAction" },
          "応募先検索アクション実行エラー",
          error
        );
      }
    },

    // キャッチコピー生成処理
    executeCatchcopyAction(): void {
      try {
        // キャッチコピー生成 → 原稿選択画面に遷移
        this.$store.dispatch("assistNavigationModule/showManuscriptSelect");
      } catch (error) {
        logError(
          { module: "AssistRequestContent", action: "executeCatchcopyAction" },
          "キャッチコピー生成アクション実行エラー",
          error
        );
      }
    },

    // 🆕 情報バナーのクローズ処理
    handleInfoBannerClose(): void {
      try {
        this.$store.dispatch("assistNavigationModule/dismissInfoBanner");
      } catch (error) {
        logError(
          { module: "AssistRequestContent", action: "handleInfoBannerClose" },
          "情報バナーのクローズエラー",
          error
        );
      }
    },
  },
});
